import { getPlansByOrder } from 'src/@api/dashboard'
import { isUpgradeDeviceId } from 'src/utils/resolveUpgradeDeviceId'

export const getPlanMonths = plan => {
  const nameMatch = plan?.name?.match(/(\d+)\s*Month/i) || plan?.name?.match(/(\d+)/)
  if (nameMatch) {
    const n = Number(nameMatch[1])
    if ([1, 3, 12].includes(n)) return n
  }

  const days = Number(plan?.valid_days)
  if (Number.isFinite(days) && days > 0) {
    if (days >= 360) return 12
    if (days >= 80) return 3

    return Math.max(1, Math.round(days / 30))
  }

  return 1
}

/** 月均价：向下取整到 2 位小数（不用四舍五入） */
const toFixed2 = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0.00'

  return (Math.floor(n * 100 + Number.EPSILON) / 100).toFixed(2)
}

/**
 * plans-by-order 展示价（月均）
 * - SEVERE（全员 lapsed）：forceExpiredOver=true，划线用 renew_price
 * - 宽限期/有效：现价 = price + setup，无划线（不再用本地 expire_time 推算是否超 30 天）
 */
export const mapRenewalPlans = (apiPlans, expireTime, { forceExpiredOver = false } = {}) => {
  const expiredOver = Boolean(forceExpiredOver)

  return (Array.isArray(apiPlans) ? apiPlans : [])
    .map(plan => {
      const months = getPlanMonths(plan)
      const renew = Number(plan.renew_price) || 0
      const setup = Number(plan.setup) || 0
      const price = Number(plan.price) || 0
      const currentTotal = price + setup
      const originalTotal = expiredOver ? renew : null

      return {
        productId: plan.id,
        name: plan.name || '',
        months,
        periodKey: months === 12 ? 'Yearly' : months === 3 ? 'Quarterly' : 'Monthly',
        periodLabelKey: months === 12 ? '12 months' : months === 3 ? '3 months' : '1 month',
        titleKey: months === 12 ? '12 Months' : months === 3 ? '3 Months' : '1 Months',
        badgeKey: months === 12 ? 'Best Value' : months === 3 ? 'Most Popular' : null,
        monthlyPrice: toFixed2(currentTotal / months),
        monthlyOriginal: originalTotal != null ? toFixed2(originalTotal / months) : null,
        showOriginal: expiredOver && originalTotal != null
      }
    })
    .sort((a, b) => a.months - b.months)
}

export const resolvePlanIndex = (plans, productName, { defaultMonths = 1 } = {}) => {
  if (!plans?.length) return 0

  // 有套餐名按数字解析；无名字用 defaultMonths（超 30 天续费页默认 3 个月，与 SpyX 一致）
  const match = productName?.match(/\d+/g)
  let months = defaultMonths
  if (match?.length) {
    if (match[0] === '12') months = 12
    else if (match[0] === '3') months = 3
    else months = 1
  }

  const idx = plans.findIndex(p => p.months === months)
  if (idx >= 0) return idx

  return 0
}

/** 按订单拉 plans-by-order，解析默认续费商品 id（renewId 必传） */
export async function fetchRenewProductIdForOrder(order, { forceExpiredOver = false } = {}) {
  if (order?.id == null || order?.id === '') return null

  try {
    const res = await getPlansByOrder({ order_id: order.id })
    const list = res?.code === 0 && Array.isArray(res.data) ? res.data : []
    if (!list.length) return null

    const expireTime = order.expire_time_raw ?? order.expire_time
    const plans = mapRenewalPlans(list, expireTime, { forceExpiredOver })
    const idx = resolvePlanIndex(plans, order.product_name)

    return plans[idx]?.productId ?? null
  } catch {
    return null
  }
}

/**
 * Msafely 官网的新购/续费入口。
 * Dashboard 不承载 /step/checkout 路由；续费使用新支付方式时，进入官网套餐页创建新订单。
 *
 * deviceId（设备包 id）必须带上：官网套餐页会把它透传到支付页，
 * 支付页建单时再带回后端，后端才会把这笔付款续到「原来那台设备」上。
 * 丢了它，后端会另开一个设备包（多绑定一个 package）。
 */
export const buildMsafelyPurchaseHref = ({ locale, deviceId } = {}) => {
  const webLocale = locale === 'it-ch' ? 'it' : locale
  const localizedPricingLocales = new Set(['ar', 'de', 'es', 'it', 'jp', 'kr', 'pt', 'ru', 'tr', 'tw'])
  const pricingPath = localizedPricingLocales.has(webLocale)
    ? `/${webLocale}/pricing`
    : process.env.NEXT_PUBLIC_MY_PRICING || '/pricing'
  const clientUrl = (process.env.NEXT_PUBLIC_CLIENT_URL || '').replace(/\/$/, '')

  // 只接受真正的设备包 id（数字/非邮箱），绝不把邮箱当 device_id 传出去
  const id = isUpgradeDeviceId(deviceId) ? String(deviceId).trim() : ''
  const deviceQuery = id ? `?device_id=${encodeURIComponent(id)}` : ''

  return `${clientUrl}${pricingPath}${deviceQuery}`
}

/**
 * Msafely 官网的「续费支付页」直达入口（跳过官网套餐页，直接进 /payment/{productId}）。
 *
 * 与 buildMsafelyPurchaseHref 的区别：
 * - buildMsafelyPurchaseHref 落在官网套餐页，用户还要再选一次套餐（用于「过期引导购买」等未选套餐的场景）
 * - 本函数用于「用户已在 Dashboard 弹窗里选好套餐」的场景（my account 的「添加卡」），少一步
 *
 * 与 SpyX 的对齐关系：SpyX 的 buildRenewCheckoutHref 落在官网 /step/checkout/{productId}，
 * 但 msafely 官网没有 /step/checkout 路由，对应的支付页路径是 /payment/{productId}。
 *
 * deviceId（设备包 id）同样必须带上，理由见 buildMsafelyPurchaseHref。
 * 路径的多语言映射与官网 configs/urlMapping.js 的 '/payment' 项保持一致（注意法语是 /fr/paiement）。
 */
const PAYMENT_LOCALE_PATH = {
  ar: '/ar/payment',
  de: '/de/payment',
  es: '/es/payment',
  fr: '/fr/paiement',
  it: '/it/payment',
  jp: '/jp/payment',
  kr: '/kr/payment',
  pt: '/pt/payment',
  ru: '/ru/payment',
  tr: '/tr/payment',
  tw: '/tw/payment'
}

export const buildMsafelyRenewCheckoutHref = ({ locale, productId, deviceId } = {}) => {
  if (productId == null || productId === '') return null

  const webLocale = locale === 'it-ch' ? 'it' : locale
  const paymentPath = PAYMENT_LOCALE_PATH[webLocale] || '/payment'
  const clientUrl = (process.env.NEXT_PUBLIC_CLIENT_URL || '').replace(/\/$/, '')

  // 只接受真正的设备包 id（数字/非邮箱），绝不把邮箱当 device_id 传出去
  const id = isUpgradeDeviceId(deviceId) ? String(deviceId).trim() : ''
  const deviceQuery = id ? `?device_id=${encodeURIComponent(id)}` : ''

  return `${clientUrl}${paymentPath}/${encodeURIComponent(productId)}${deviceQuery}`
}
